"use client";

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  KEYBOARD_VIEWBOX_HEIGHT,
  keyboardViewBoxWidth,
  MiniPiano,
} from "@/components/ui/molecules/mini-piano";
import { Term } from "@/components/ui/organisms/harmony/harmony-term";
import { useNoteNotation } from "@/hooks/use-notation";
import { useHintPublisher } from "@/hooks/use-hint-publisher";
import {
  useHandsOnControls,
  useHandsOnPiano,
} from "@/hooks/journey/use-hands-on-controls";
import type { Hint } from "@/contexts/hint-context";
import type { AnalysisChord } from "@/lib/music/analyzer-types";
import { chordSymbolLabel } from "@/lib/music/notation";
import type { LearningJourney } from "@/lib/learning-journey/journey";
import type { Phrase, PhraseFSMSnapshot } from "@/lib/learning-journey/types";
import {
  applicableStepIndices,
  phraseStateToStepIndex,
  reachableStepIndices,
  stepIndexToPhraseState,
  stepPosition,
} from "@/lib/learning-journey/step-mapping";
import type { RangeRunner } from "@/lib/learning-journey/runtime/range-runner";
import type { Tonality } from "@/lib/music/tonality";
import { formatTonalityLabel } from "../intro-harmony-info";
import {
  buildPhraseChords,
  chordDrawing,
  clampKeyboardWindow,
  phraseKeyboardWindow,
  type KeyboardWindow,
  type PhraseChord,
} from "../phrase-chords";
import { pitchRailVariant, pitchRailY } from "../pitch-rail";
import { PitchRail } from "./pitch-rail";
import { computeS0Dosage, type S0Density } from "../s0-density";
import { chordHandColors, chordHints, HAND_CSS_COLORS } from "../s0-hints";
import {
  bassNoteLabel,
  chordNoteNames,
  chordProseLabel,
  degreeGloss,
  focusLine,
  isGlossable,
  passageLine,
  phraseHook,
  S0_TERMS,
} from "../s0-copy";
import { readFlashSeen, writeFlashSeen } from "../s0-preferences";
import { FloatingHeader } from "../shell/floating-header";
import { FloatingShell } from "../shell/floating-shell";
import { MiniSteps } from "../shell/mini-steps";
import { useS0Walkthrough, type S0Walkthrough } from "./use-s0-walkthrough";

/**
 * View for state `S0`: the first contact with a phrase, before a single
 * note is played.
 *
 * ## What this screen is for
 *
 * Linking the phrase to the harmony of the piece — which is Virtuosa's
 * whole thesis, applied at the smallest scale. It replaces a static list
 * of chord names, whose one highlighted row asserted a "current chord"
 * that did not exist while the keyboard lit every note of the phrase at
 * once.
 *
 * The frieze fixes that on three counts. Cell width is proportional to
 * how long the chord actually lasts, so harmonic rhythm becomes visible
 * without any vocabulary. Each cell carries a `MiniPiano` of the notes
 * really played — chords in this repertoire are often incomplete or
 * inverted, and a bare symbol hides that. And because a playhead runs
 * across it, "the current chord" finally exists, so the keyboard can
 * light one chord at a time and mean it.
 *
 * ## Dosage
 *
 * S0 runs once per phrase, 20-40 times per piece. `computeS0Dosage`
 * decides how much to show; `flash` collapses the whole thing to a
 * banner. See `s0-density.ts`.
 */

// Panel geometry. The floating shell is wide and short on purpose: the
// old full-height sidebar covered the piano roll during the very screen
// meant to connect the player to it.
const PANEL_WIDTH_PX = 560;
const PANEL_PADDING_X_PX = 28;
const FRIEZE_WIDTH_PX = PANEL_WIDTH_PX - PANEL_PADDING_X_PX * 2;
/**
 * Gutter between frieze cells. Wider than a hairline on purpose: the cells
 * are discrete objects now, and the rail below is what carries continuity.
 */
const FRIEZE_GAP_PX = 6;
const CELL_PADDING_X_PX = 6;

/** Keyboard height bounds inside a frieze cell. */
const KEYBOARD_MIN_H_PX = 16;
const KEYBOARD_MAX_H_PX = 40;
/** Under this, a white key is a smudge and the cell drops the keyboard. */
const MIN_WHITE_KEY_PX = 4;

/**
 * Beyond this the frieze narrows its window (see `clampKeyboardWindow`):
 * a four-octave keyboard in a ~110px cell has sub-4px keys.
 */
const FRIEZE_MAX_OCTAVES = 3;

/**
 * Past this, `MiniPiano`'s labels render under ~8px and stop being
 * readable, so the focus block lists the note names underneath instead.
 */
const MAX_OCTAVES_FOR_KEY_LABELS = 3;

/**
 * The focus keyboard's height cap. Mirrors its `max-h-[144px]` class, which
 * has to stay a literal for Tailwind to emit it.
 */
const FOCUS_KEYBOARD_MAX_H_PX = 144;

const RAIL_H_PX = 4;
const RAIL_GAP_PX = 2;
/** Visibility floor for a rail segment — one order of magnitude below the
 *  64px readability floor the cells used to need, so it barely distorts. */
const RAIL_SEGMENT_MIN_PX = 3;

/** Playback rate per density: denser screen, slower read. */
const RATE_BY_DENSITY: Readonly<Record<S0Density, number>> = {
  dense: 0.75,
  standard: 0.85,
  flash: 1,
};

/** Grace period before a flash-mode screen advances on its own. */
const AUTO_ADVANCE_MS = 250;

/**
 * Height reserved for the lines between the chord name and the keyboard.
 * Reserving it keeps the panel — and the keyboard under the text — from
 * moving as the pointer crosses the frieze.
 *
 * Compact: the function and the passage, one line each. Full: plus the
 * explanation, three lines at 12px/1.55 at worst (the inversion sentence).
 * The note-name line is added on top when the window is too wide for labels
 * on the keys, which is a property of the phrase, not of the chord.
 */
const FOCUS_TEXT_COMPACT_MIN_H_PX = 44;
const FOCUS_TEXT_MIN_H_PX = 110;
const NOTE_NAMES_LINE_H_PX = 22;

const HINT_SOURCE = "journey-s0";

type ChordPresentationProps = {
  readonly phrase: Phrase;
  readonly phraseSnapshot: PhraseFSMSnapshot;
  readonly journey: LearningJourney;
  readonly chords: readonly AnalysisChord[];
  readonly phrases: readonly Phrase[];
  readonly phraseIndex: number;
  readonly tonality: Tonality | null;
  readonly ticksPerBeat: number;
  readonly ticksPerBar: number;
  readonly rangeRunner: RangeRunner | null;
  readonly songId: string;
  readonly onAcknowledge: () => void;
};

export function ChordPresentation({
  phrase,
  phraseSnapshot,
  journey,
  chords,
  phrases,
  phraseIndex,
  tonality,
  ticksPerBeat,
  ticksPerBar,
  rangeRunner,
  songId,
  onAcknowledge,
}: ChordPresentationProps) {
  const titleId = useId();
  const notation = useNoteNotation();

  const [flashSeen, setFlashSeen] = useState(false);

  // Read the persisted flag after mount: `localStorage` is unavailable during
  // the server render, and reading it in state init would desync hydration.
  useEffect(() => {
    setFlashSeen(readFlashSeen(songId));
  }, [songId]);

  const phraseChords = useMemo(
    () => buildPhraseChords(chords, phrase),
    [chords, phrase],
  );

  // The focus block draws the phrase's full range; the frieze narrows to at
  // most three octaves so its small keyboards keep readable keys. What is
  // non-negotiable is that all *frieze* cells share one window — that is
  // what makes the hand shapes comparable from chord to chord.
  const focusWindow = useMemo(
    () => phraseKeyboardWindow(phraseChords),
    [phraseChords],
  );

  const friezeWindow = useMemo(
    () =>
      clampKeyboardWindow(
        focusWindow,
        FRIEZE_MAX_OCTAVES,
        phraseChords.flatMap((e) => [...e.midis]),
      ),
    [focusWindow, phraseChords],
  );

  // Internal bar lines, for the rail's tick marks.
  const measureTicks = useMemo(() => {
    if (ticksPerBar <= 0) {
      return [];
    }

    const out: number[] = [];

    for (
      let tick = Math.ceil(phrase.startTick / ticksPerBar) * ticksPerBar;
      tick < phrase.endTick;
      tick += ticksPerBar
    ) {
      if (tick > phrase.startTick) {
        out.push(tick);
      }
    }

    return out;
  }, [phrase.startTick, phrase.endTick, ticksPerBar]);

  const dosage = useMemo(
    () =>
      computeS0Dosage({
        chords,
        phrases,
        phraseIndex,
        ticksPerBeat,
      }),
    [chords, phrases, phraseIndex, ticksPerBeat],
  );

  const [interacted, setInteracted] = useState(false);
  const markInteracted = useCallback(() => setInteracted(true), []);

  // The first flash of a piece explains itself instead of vanishing.
  const teachingFlash = dosage.density === "flash" && !flashSeen;
  const autoAdvance = dosage.density === "flash" && flashSeen;

  const handlePlaybackEnd = useCallback(() => {
    if (teachingFlash) {
      writeFlashSeen(songId);
      setFlashSeen(true);
    }
  }, [songId, teachingFlash]);

  const walkthrough = useS0Walkthrough({
    phraseChords,
    phrase,
    rangeRunner,
    rate: RATE_BY_DENSITY[dosage.density],
    autoPlay: true,
    onPlaybackEnd: handlePlaybackEnd,
  });

  // The keyboard follows the *music*, not the pointer: hovering the frieze
  // must not relight the studio piano.
  const currentEntry =
    walkthrough.currentIndex === null
      ? null
      : phraseChords[walkthrough.currentIndex] ?? null;

  const focusEntry =
    walkthrough.focusIndex === null
      ? null
      : phraseChords[walkthrough.focusIndex] ?? null;

  usePublishChordHint(currentEntry);
  useS0Shortcuts({ walkthrough, onAcknowledge, onInteract: markInteracted });

  // The pedal does here exactly what `Entrée` does: it starts the phrase.
  //
  // Playing is deliberately left alone on this screen. S0 is where the player
  // hears the harmony and tries it under their fingers — a note is
  // exploration, not an answer — and a screen that walked forward on it would
  // punish the one behaviour it is trying to produce.
  useHandsOnControls({
    enabled: !walkthrough.isPlaying,
    onAdvance: onAcknowledge,
  });
  useAutoAdvance({
    enabled: autoAdvance && !interacted,
    isPlaying: walkthrough.isPlaying,
    onAdvance: onAcknowledge,
  });

  const handleSelectChord = useCallback(
    (index: number) => {
      markInteracted();
      walkthrough.select(index);
    },
    [markInteracted, walkthrough],
  );

  // Same frame as every other step of the unit — header, stepper — so the
  // first screen does not read as a different kind of object from the second.
  const stepIndex = phraseStateToStepIndex(phraseSnapshot.state);
  const steps = useMemo(
    () => applicableStepIndices(phrase, phraseSnapshot.chains),
    [phrase, phraseSnapshot.chains],
  );
  const reachable = useMemo(
    () =>
      reachableStepIndices(
        phrase,
        phraseSnapshot.maxState,
        phraseSnapshot.chains,
      ),
    [phrase, phraseSnapshot.maxState, phraseSnapshot.chains],
  );

  const handleJump = useCallback(
    (idx: number) => {
      const target = stepIndexToPhraseState(idx);

      if (target === null) {
        return;
      }

      journey.revisitPhraseState(target);
    },
    [journey],
  );

  const handleBack = useCallback(() => {
    journey.stepBack();
  }, [journey]);

  const shell = (children: React.ReactNode) => (
    <FloatingShell
      variant="floating"
      width={PANEL_WIDTH_PX}
      ariaLabel="Présentation de la phrase"
      titleId={titleId}
      contentKey={`s0-${dosage.density}`}
    >
      <FloatingHeader
        stepIndex={stepPosition(steps, stepIndex)}
        stepTotal={steps.length}
        kicker="ACCORDS"
        titleId={titleId}
        trailing={
          <PhraseLocation
            phraseIndex={phraseIndex}
            phraseTotal={phrases.length}
            phrase={phrase}
            tonality={dosage.density === "flash" ? null : tonality}
            notation={notation}
          />
        }
      />

      <div
        onMouseMove={markInteracted}
        style={{ padding: `20px ${PANEL_PADDING_X_PX}px 18px` }}
      >
        {children}
      </div>

      <MiniSteps
        active={stepIndex}
        steps={steps}
        reachable={reachable}
        onJump={handleJump}
        onBack={phraseSnapshot.canStepBack ? handleBack : undefined}
      />
    </FloatingShell>
  );

  if (dosage.density === "flash") {
    return shell(
      <FlashBanner
        phrase={phrase}
        phraseChords={phraseChords}
        dosage={dosage}
        teaching={teachingFlash}
        walkthrough={walkthrough}
        onAcknowledge={onAcknowledge}
      />,
    );
  }

  return shell(
    <>
      {focusEntry !== null && (
        <FocusBlock
          entry={focusEntry}
          keyboardWindow={focusWindow}
          tonality={tonality}
          notation={notation}
          ticksPerBeat={ticksPerBeat}
          ticksPerBar={ticksPerBar}
          isNew={dosage.newSymbols.has(focusEntry.chord.symbol)}
          showFunction={!dosage.busy}
          showExplanation={!dosage.busy && dosage.density === "dense"}
        />
      )}

      {/* The phrase comes after the chord: the chord is what is under the
          hands now, the frieze is the list to walk through. */}
      <div
        className="mt-4"
        style={{
          paddingTop: 12,
          borderTop: "1px solid var(--color-border-subtle)",
        }}
      >
        <Hook
          phraseChords={phraseChords}
          tonality={tonality}
          phrase={phrase}
          notation={notation}
          busy={dosage.busy}
        />

        {/* One chord means no progression: a single cell and a single-segment
            rail say nothing, so the focus block carries it alone. */}
        {phraseChords.length > 1 && (
          <>
            <Frieze
              phraseChords={phraseChords}
              keyboardWindow={friezeWindow}
              currentIndex={walkthrough.currentIndex}
              hoveredIndex={walkthrough.hoveredIndex}
              pinnedIndex={walkthrough.pinnedIndex}
              flashIndex={walkthrough.flashIndex}
              newSymbols={dosage.newSymbols}
              onHover={walkthrough.hover}
              onSelect={handleSelectChord}
            />

            <Rail
              phraseChords={phraseChords}
              currentIndex={walkthrough.currentIndex}
              hoveredIndex={walkthrough.hoveredIndex}
              isPlaying={walkthrough.isPlaying}
              measureTicks={measureTicks}
              onHover={walkthrough.hover}
              onSelect={handleSelectChord}
            />
          </>
        )}
      </div>

      <BottomBar
        phrase={phrase}
        density={dosage.density}
        degraded={walkthrough.degraded}
        onReplay={() => {
          markInteracted();
          walkthrough.replay();
        }}
        onAcknowledge={onAcknowledge}
      />
    </>,
  );
}

// ── Sections ──────────────────────────────────────────────────────

/**
 * `PHRASE 3 / 8 · MES. 9-12 · SI MINEUR`, on the right of the header.
 *
 * The tonality is not decoration: without it, `iv` and `III` are
 * literally meaningless.
 */
function PhraseLocation({
  phraseIndex,
  phraseTotal,
  phrase,
  tonality,
  notation,
}: {
  phraseIndex: number;
  phraseTotal: number;
  phrase: Phrase;
  tonality: Tonality | null;
  notation: ReturnType<typeof useNoteNotation>;
}) {
  const parts = [
    `Phrase ${phraseIndex + 1} / ${phraseTotal}`,
    `Mes. ${phrase.startMeasure}-${phrase.endMeasure}`,
  ];

  if (tonality !== null) {
    parts.push(formatTonalityLabel(tonality, notation));
  }

  return (
    <span
      className="font-mono text-[11px] uppercase shrink-0"
      style={{
        letterSpacing: "0.16em",
        color: "var(--color-text-muted)",
      }}
    >
      {parts.join(" · ")}
    </span>
  );
}

function Hook({
  phraseChords,
  tonality,
  phrase,
  notation,
  busy,
}: {
  phraseChords: readonly PhraseChord[];
  tonality: Tonality | null;
  phrase: Phrase;
  notation: ReturnType<typeof useNoteNotation>;
  busy: boolean;
}) {
  const measureCount = phrase.endMeasure - phrase.startMeasure + 1;
  const text = busy
    ? `Ça bouge vite : ${phraseChords.length} accords en ${measureCount} mesures.`
    : phraseHook(phraseChords, tonality, measureCount, notation);

  if (text === null) {
    return null;
  }

  return (
    <p
      style={{
        margin: 0,
        fontSize: 14,
        lineHeight: 1.5,
        color: "var(--color-text-secondary)",
        textWrap: "pretty",
      }}
    >
      {text}
    </p>
  );
}

/**
 * The frieze: one cell per chord, **all exactly the same size**.
 *
 * Cells used to be as wide as the chord was long. The intent was to make
 * harmonic rhythm visible without vocabulary, but width in a UI means "what
 * the content needs", so an undeclared measurement read as a rendering bug —
 * and worse, `MiniPiano` letterboxes, so unequal cells produced keyboards at
 * different scales, destroying the very comparability the shared octave
 * window exists to guarantee.
 *
 * Duration moved to `Rail` below, which is a channel that announces itself
 * as a measurement and can be exactly proportional. The cells now carry only
 * identity: symbol, degree, voicing.
 */
function Frieze({
  phraseChords,
  keyboardWindow,
  currentIndex,
  hoveredIndex,
  pinnedIndex,
  flashIndex,
  newSymbols,
  onHover,
  onSelect,
}: {
  phraseChords: readonly PhraseChord[];
  keyboardWindow: KeyboardWindow;
  currentIndex: number | null;
  hoveredIndex: number | null;
  pinnedIndex: number | null;
  flashIndex: number | null;
  newSymbols: ReadonlySet<string>;
  onHover: (index: number | null) => void;
  onSelect: (index: number) => void;
}) {
  const count = phraseChords.length;
  const cellWidth = (FRIEZE_WIDTH_PX - (count - 1) * FRIEZE_GAP_PX) / count;
  const contentWidth = cellWidth - CELL_PADDING_X_PX * 2;
  const geometry = keyboardGeometry(contentWidth, keyboardWindow.octaves);

  return (
    <ul
      className="mt-3 flex list-none p-0"
      aria-label="Enchaînement de la phrase"
      // One handler on the container, not per cell: with a 6px gutter,
      // per-cell handlers would blink the focus to null on every crossing.
      onMouseLeave={() => onHover(null)}
      style={{ gap: FRIEZE_GAP_PX }}
    >
      {phraseChords.map((entry, index) => (
        <li key={`${entry.chord.id}-${index}`} style={{ width: cellWidth }}>
          <FriezeCell
            entry={entry}
            index={index}
            geometry={geometry}
            window={keyboardWindow}
            current={index === currentIndex}
            hovered={index === hoveredIndex}
            pinned={index === pinnedIndex}
            flashing={index === flashIndex}
            isNew={newSymbols.has(entry.chord.symbol)}
            onHover={onHover}
            onSelect={onSelect}
          />
        </li>
      ))}
    </ul>
  );
}

/** How the little keyboard is drawn inside a cell of a given width. */
type KeyboardGeometry = {
  /** `null` when keys would be too narrow to read; the cell hides them. */
  readonly height: number | null;
  /** Set when the height hit its cap, so the drawing is centred instead. */
  readonly width: number | null;
};

/**
 * Derive the keyboard size rather than let `preserveAspectRatio` letterbox.
 *
 * `MiniPiano` scales its drawing to fit and centres the remainder. Left to
 * itself in a fixed-height box it leaves large empty margins and, across
 * differently-sized boxes, different key sizes. Computing the height from
 * the width makes the drawing fill the cell exactly, and since every cell is
 * now the same width, every keyboard is identical to the pixel.
 */
function keyboardGeometry(
  contentWidth: number,
  octaves: number,
): KeyboardGeometry {
  const whiteKeyPx = contentWidth / (7 * octaves);

  // Below this a keyboard is a smudge, not information. The cell drops it
  // and the voicing is carried by the focus block alone.
  if (whiteKeyPx < MIN_WHITE_KEY_PX) {
    return { height: null, width: null };
  }

  const natural = Math.round((contentWidth * 56) / (98 * octaves));

  if (natural <= KEYBOARD_MAX_H_PX) {
    return { height: Math.max(KEYBOARD_MIN_H_PX, natural), width: null };
  }

  // Capped: pin the width too so the drawing stays flush and centred.
  return {
    height: KEYBOARD_MAX_H_PX,
    width: Math.round((KEYBOARD_MAX_H_PX * 98 * octaves) / 56),
  };
}

function FriezeCell({
  entry,
  index,
  geometry,
  window,
  current,
  hovered,
  pinned,
  flashing,
  isNew,
  onHover,
  onSelect,
}: {
  entry: PhraseChord;
  index: number;
  geometry: KeyboardGeometry;
  window: KeyboardWindow;
  current: boolean;
  hovered: boolean;
  pinned: boolean;
  flashing: boolean;
  isNew: boolean;
  onHover: (index: number | null) => void;
  onSelect: (index: number) => void;
}) {
  // The player's note notation, not the chord-symbol one: the focus block
  // names this same chord in words right above, and « Ré majeur » over a
  // cell reading `D` made two spellings of one chord on one panel.
  const notation = useNoteNotation();
  const handColors = useMemo(() => chordHandColors(entry), [entry]);
  const drawing = useMemo(() => chordDrawing(entry), [entry]);
  // Bound once: a narrowing on `geometry.height` would not survive into the
  // trajectories callback below, where the rail needs the height as a number.
  const keyboardHeight = geometry.height;
  const showDegree = isGlossable(entry) && keyboardHeight !== null;

  // Violet says "this is where the music is"; white says "this is where
  // your pointer is". They compose instead of overwriting each other.
  const background = current
    ? hovered
      ? "rgba(139, 92, 246, 0.14)"
      : "rgba(139, 92, 246, 0.10)"
    : hovered
      ? "rgba(255, 255, 255, 0.06)"
      : "rgba(255, 255, 255, 0.025)";

  // The pinned chord — "you chose this", as opposed to "playback went past
  // here" — is the one with the full outline. It used to be a 2px bar along
  // the top edge only, which read as a rendering glitch on a rounded cell.
  const borderColor = pinned
    ? "#a78bfa"
    : current
    ? hovered
      ? "rgba(139, 92, 246, 0.62)"
      : "rgba(139, 92, 246, 0.40)"
    : hovered
      ? "rgba(255, 255, 255, 0.14)"
      : "transparent";

  return (
    <button
      type="button"
      onMouseEnter={() => onHover(index)}
      onFocus={() => onHover(index)}
      onClick={() => onSelect(index)}
      aria-current={current}
      aria-pressed={pinned}
      aria-label={friezeCellLabel(entry)}
      className="flex w-full cursor-pointer flex-col text-left focus:outline-none focus-visible:outline-2"
      style={{
        padding: `7px ${CELL_PADDING_X_PX}px`,
        borderRadius: 8,
        // Always present so switching states never reflows the row.
        border: `1px solid ${borderColor}`,
        background,
        transitionProperty: "background-color, border-color, color, transform",
        transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        // Asymmetric: crisp on the way in, settled on the way out, so
        // sweeping across the row does not shimmer.
        transitionDuration: hovered || current ? "110ms" : "200ms",
        ["--tw-outline-color" as string]: "var(--color-violet-text)",
      }}
    >
      <span
        className="flex w-full items-baseline"
        style={{ gap: 5, height: 18 }}
      >
        <span
          style={{
            fontSize: 15,
            fontWeight: current ? 500 : 400,
            letterSpacing: "-0.02em",
            color:
              current || hovered
                ? "var(--color-text-primary)"
                : "var(--color-text-secondary)",
            whiteSpace: "nowrap",
          }}
        >
          {chordSymbolLabel(entry.chord.symbol, notation)}
        </span>

        {showDegree && (
          <span
            className="font-mono"
            style={{
              fontSize: 10,
              fontStyle: "italic",
              color: "var(--color-text-muted)",
            }}
          >
            {entry.chord.romanNumeral}
          </span>
        )}

        <span className="flex-1" aria-hidden />

        {/* A dot, not the word: a text line would add height to some cells
            only and leave the row ragged. The word is spelled out in the
            focus block, which auto-play walks through anyway. */}
        {isNew && (
          <span
            aria-hidden
            style={{
              width: 5,
              height: 5,
              borderRadius: 999,
              flexShrink: 0,
              background: "var(--color-accent-bright)",
            }}
          />
        )}
      </span>

      {keyboardHeight !== null && (
        <span
          className="mt-1.5 flex flex-col items-center"
          style={{
            // Brief flare on click: the ~900ms preview otherwise produces
            // no visible change at all, which reads as a dead control.
            filter: flashing ? "brightness(1.55) saturate(1.1)" : "none",
            transition: "filter 240ms ease-out",
          }}
        >
          {/* The rail is drawn *on* the keys it travels across. Lighting
              them instead would claim a hand position that existed at no
              single instant. */}
          <span
            style={{
              position: "relative",
              display: "block",
              height: keyboardHeight,
              width: geometry.width ?? "100%",
            }}
          >
            <MiniPiano
              midis={[...drawing.positionMidis]}
              lowMidi={window.lowMidi}
              octaves={window.octaves}
              midiColors={handColors}
              style={{ height: "100%", width: "100%" }}
            />

            {drawing.trajectories.map((t) => (
              <PitchRail
                key={t.hand}
                notes={t.notes}
                lowMidi={window.lowMidi}
                octaves={window.octaves}
                color={HAND_CSS_COLORS[t.hand]}
                variant={pitchRailVariant(
                  keyboardHeight,
                  drawing.trajectories.length,
                )}
                y={pitchRailY(t.hand, drawing.trajectories.length)}
              />
            ))}
          </span>
        </span>
      )}
    </button>
  );
}

function friezeCellLabel(entry: PhraseChord): string {
  const degree = entry.chord.romanNumeral;
  const parts = [entry.chord.symbol];

  if (degree !== "") {
    parts.push(`degré ${degree}`);
  }

  parts.push(`mesure ${entry.chord.measure}`);

  return parts.join(", ");
}

/**
 * The time channel: one segment per chord, **exactly** proportional to its
 * clipped duration.
 *
 * A segmented rail declares itself as a measurement — the vocabulary is
 * already known from chapter bars and progress segments — where an unequal
 * row of cards did not. And because a segment has no content to hold, it
 * needs no readability floor, so the proportions are the true ones instead
 * of the compressed ones the 64px floor used to produce.
 */
function Rail({
  phraseChords,
  currentIndex,
  hoveredIndex,
  isPlaying,
  measureTicks,
  onHover,
  onSelect,
}: {
  phraseChords: readonly PhraseChord[];
  currentIndex: number | null;
  hoveredIndex: number | null;
  isPlaying: boolean;
  measureTicks: readonly number[];
  onHover: (index: number | null) => void;
  onSelect: (index: number) => void;
}) {
  const start = phraseChords[0].startTick;
  const end = phraseChords[phraseChords.length - 1].endTick;
  const span = end - start;

  return (
    <div className="mt-2.5" aria-hidden>
      <div
        className="flex"
        style={{ height: RAIL_H_PX, gap: RAIL_GAP_PX }}
        onMouseLeave={() => onHover(null)}
      >
        {phraseChords.map((entry, index) => (
          <span
            key={`${entry.chord.id}-${index}`}
            onMouseEnter={() => onHover(index)}
            onClick={() => onSelect(index)}
            style={{
              flexGrow: entry.spanTicks,
              flexBasis: 0,
              minWidth: RAIL_SEGMENT_MIN_PX,
              borderRadius: 2,
              cursor: "pointer",
              background: railSegmentColor({
                index,
                currentIndex,
                hovered: index === hoveredIndex,
                isPlaying,
              }),
              transition:
                "background-color 180ms cubic-bezier(0.4, 0, 0.2, 1)",
              // A chord running past the phrase edge fades out rather than
              // claiming a hard boundary it does not have.
              maskImage: edgeMask(entry),
              WebkitMaskImage: edgeMask(entry),
            }}
          />
        ))}
      </div>

      {span > 0 && measureTicks.length > 0 && (
        <div style={{ position: "relative", height: 3, marginTop: 3 }}>
          {measureTicks.map((tick) => (
            <span
              key={tick}
              style={{
                position: "absolute",
                left: `${((tick - start) / span) * 100}%`,
                top: 0,
                width: 1,
                height: 3,
                background: "rgba(255, 255, 255, 0.10)",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function railSegmentColor({
  index,
  currentIndex,
  hovered,
  isPlaying,
}: {
  index: number;
  currentIndex: number | null;
  hovered: boolean;
  isPlaying: boolean;
}): string {
  if (index === currentIndex) {
    return hovered ? "#a78bfa" : "#8b5cf6";
  }

  if (hovered) {
    return "rgba(255, 255, 255, 0.32)";
  }

  // "Already played" only means something while something is playing; at
  // rest the pointer drives, so every inactive segment reads the same.
  if (isPlaying && currentIndex !== null) {
    return index < currentIndex
      ? "rgba(255, 255, 255, 0.18)"
      : "rgba(255, 255, 255, 0.07)";
  }

  return "rgba(255, 255, 255, 0.10)";
}

function edgeMask(entry: PhraseChord): string | undefined {
  if (entry.clippedStart) {
    return "linear-gradient(to right, transparent 0, black 10px)";
  }

  if (entry.clippedEnd) {
    return "linear-gradient(to left, transparent 0, black 10px)";
  }

  return undefined;
}

/**
 * The chord being looked at, from the top down: its name in large type, what
 * it does in the key, how the passage plays it, then the keyboard.
 *
 * The function and the passage are two lines, not one explanation chosen
 * between them: one says what the chord is *for*, the other what the hands
 * *do*, and picking one meant the other was always missing.
 */
function FocusBlock({
  entry,
  keyboardWindow,
  tonality,
  notation,
  ticksPerBeat,
  ticksPerBar,
  isNew,
  showFunction,
  showExplanation,
}: {
  entry: PhraseChord;
  keyboardWindow: KeyboardWindow;
  tonality: Tonality | null;
  notation: ReturnType<typeof useNoteNotation>;
  ticksPerBeat: number;
  ticksPerBar: number;
  isNew: boolean;
  showFunction: boolean;
  showExplanation: boolean;
}) {
  const handColors = useMemo(() => chordHandColors(entry), [entry]);
  const drawing = useMemo(() => chordDrawing(entry), [entry]);
  // The keyboard spans the panel's content width and caps at 144px, so it
  // renders between 144px (one or two octaves) and 72px (four) — always in
  // `lg` territory. Derived rather than hardcoded so a narrower panel or a
  // wider window would move the rail down a size on its own.
  const keyboardHeightPx = Math.min(
    FOCUS_KEYBOARD_MAX_H_PX,
    (FRIEZE_WIDTH_PX * KEYBOARD_VIEWBOX_HEIGHT) /
      keyboardViewBoxWidth(keyboardWindow.octaves),
  );
  const name = chordProseLabel(entry, notation);
  const bass = bassNoteLabel(entry, notation);
  const degree = isGlossable(entry) ? entry.chord.romanNumeral : null;
  const gloss = showFunction ? degreeGloss(entry, tonality) : null;
  const passage = passageLine(entry, ticksPerBeat, ticksPerBar);
  const detail = showExplanation ? focusLine(entry, notation) : null;

  // Which channel carries the note names. `MiniPiano` sizes its text in
  // viewBox units, so the wider the window the smaller the rendered label —
  // roughly 15px at two octaves, 10px at three, under 8px at four. Past
  // that the names move to a line of text rather than disappearing.
  //
  // In a row a name is read left to right as an order of play, so the line
  // says its order out loud — and `chordNoteNames` returns `null` rather
  // than list a window it cannot summarise.
  const showLabels = keyboardWindow.octaves <= MAX_OCTAVES_FOR_KEY_LABELS;
  const noteNames = showLabels ? null : chordNoteNames(entry, notation);
  const textMinHeight =
    (showExplanation ? FOCUS_TEXT_MIN_H_PX : FOCUS_TEXT_COMPACT_MIN_H_PX) +
    (showLabels ? 0 : NOTE_NAMES_LINE_H_PX);

  return (
    <div className="mt-3">
      <div className="flex items-baseline" style={{ gap: 10 }}>
        <span
          style={{
            fontSize: 30,
            lineHeight: 1.15,
            fontWeight: 600,
            letterSpacing: "-0.02em",
            color: "var(--color-text-primary)",
          }}
        >
          {name}
        </span>

        {bass !== null && (
          <span style={{ fontSize: 15, color: "var(--color-text-secondary)" }}>
            posé sur un {bass}
          </span>
        )}

        {degree !== null && (
          <span
            className="font-mono"
            style={{
              fontSize: 15,
              fontStyle: "italic",
              color: "var(--color-text-muted)",
            }}
          >
            {degree}
          </span>
        )}

        <span className="flex-1" aria-hidden />

        {isNew && (
          <span style={{ fontSize: 11, color: "var(--color-violet-text)" }}>
            Nouveau dans le morceau
          </span>
        )}
      </div>

      <div style={{ minHeight: textMinHeight, marginTop: 4 }}>
        {gloss !== null && (
          <p
            style={{
              margin: 0,
              fontSize: 15,
              lineHeight: 1.5,
              color: "var(--color-text-secondary)",
            }}
          >
            {capitalize(gloss)}
          </p>
        )}

        {passage !== null && (
          <p
            style={{
              margin: gloss !== null ? "2px 0 0" : 0,
              fontSize: 13,
              lineHeight: 1.5,
              color: "var(--color-text-muted)",
            }}
          >
            {passage}
          </p>
        )}

        {noteNames !== null && (
          <p
            style={{
              margin: "2px 0 0",
              fontSize: 12,
              lineHeight: 1.6,
              color: "var(--color-text-secondary)",
              letterSpacing: "0.01em",
            }}
          >
            Du grave à l&apos;aigu : {noteNames.join(" · ")}
          </p>
        )}

        {detail !== null && (
          <p
            className="mt-2"
            style={{
              fontSize: 12,
              lineHeight: 1.55,
              color: "var(--color-text-muted)",
              textWrap: "pretty",
            }}
          >
            {detail.before}
            {detail.term !== "" && (
              <Term tip={S0_TERMS[detail.term] ?? ""}>{detail.term}</Term>
            )}
            {detail.after}
          </p>
        )}
      </div>

      {/* Same octave window as the frieze — this is the same keyboard,
          just larger. Sized to span the full width so it sits edge to edge
          like the frieze, instead of floating letterboxed in the middle.

          Coloured by hand, like the studio keyboard below: a chord holding
          for two bars collects a dozen keys, and one flat accent turns
          that into a block nobody can parse. */}
      <div className="relative mt-3">
        <MiniPiano
          midis={[...drawing.positionMidis]}
          lowMidi={keyboardWindow.lowMidi}
          octaves={keyboardWindow.octaves}
          midiColors={handColors}
          showLabels={showLabels}
          className="max-h-[144px] w-full"
        />

        {drawing.trajectories.map((t) => (
          <PitchRail
            key={t.hand}
            notes={t.notes}
            lowMidi={keyboardWindow.lowMidi}
            octaves={keyboardWindow.octaves}
            color={HAND_CSS_COLORS[t.hand]}
            variant={pitchRailVariant(
              keyboardHeightPx,
              drawing.trajectories.length,
            )}
            y={pitchRailY(t.hand, drawing.trajectories.length)}
          />
        ))}
      </div>
    </div>
  );
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function BottomBar({
  phrase,
  density,
  degraded,
  onReplay,
  onAcknowledge,
}: {
  phrase: Phrase;
  density: S0Density;
  degraded: boolean;
  onReplay: () => void;
  onAcknowledge: () => void;
}) {
  const leftCount = phrase.notesByHand.left.length;
  const rightCount = phrase.notesByHand.right.length;
  const handsOn = useHandsOnPiano();

  return (
    <div
      className="mt-4 flex items-center"
      style={{
        gap: 12,
        justifyContent: "space-between",
        paddingTop: 4,
        paddingBottom: 4,
      }}
    >
      {density !== "flash" && (
        <span
          className="font-mono"
          style={{ fontSize: 10, color: "var(--color-text-muted)" }}
        >
          {/* Tinted to double as the legend for the keyboards above: the
              colours decode themselves instead of needing a caption. */}
          <span style={{ color: HAND_CSS_COLORS.left }}>MG</span> {leftCount}{" "}
          notes · <span style={{ color: HAND_CSS_COLORS.right }}>MD</span>{" "}
          {rightCount} notes
        </span>
      )}

      <span className="flex items-center" style={{ gap: 10 }}>
        <button
          type="button"
          onClick={onReplay}
          className="flex items-center focus:outline-none focus-visible:ring-2"
          style={{
            gap: 6,
            padding: "8px 12px",
            borderRadius: 8,
            background: "transparent",
            border: "1px solid var(--color-border-subtle)",
            color: "var(--color-text-secondary)",
            fontSize: 12,
            ["--tw-ring-color" as string]: "var(--color-violet-text)",
          }}
        >
          <span>{degraded ? "Écouter accord par accord" : "Réécouter"}</span>
          <KeyBadge>espace</KeyBadge>
        </button>

        <button
          type="button"
          onClick={onAcknowledge}
          className="flex items-center focus:outline-none focus-visible:ring-2"
          style={{
            gap: 8,
            padding: "10px 16px",
            borderRadius: 8,
            background: "var(--color-accent)",
            color: "#ffffff",
            fontSize: 13,
            fontWeight: 500,
            boxShadow: "0 0 22px var(--color-violet-glow)",
            ["--tw-ring-color" as string]: "var(--color-violet-text)",
          }}
        >
          <span>Commencer</span>
          <KeyBadge>↵</KeyBadge>
          {handsOn && <KeyBadge>pédale</KeyBadge>}
        </button>
      </span>
    </div>
  );
}

/**
 * Flash mode: one 48px band for a progression already met.
 *
 * The keyboard still walks the chords — the cheap part is kept, only the
 * reading material is dropped.
 */
function FlashBanner({
  phrase,
  phraseChords,
  dosage,
  teaching,
  walkthrough,
  onAcknowledge,
}: {
  phrase: Phrase;
  phraseChords: readonly PhraseChord[];
  dosage: ReturnType<typeof computeS0Dosage>;
  teaching: boolean;
  walkthrough: S0Walkthrough;
  onAcknowledge: () => void;
}) {
  const notation = useNoteNotation();
  const newSymbol = [...dosage.newSymbols][0];

  return (
    <div>
      {dosage.repeatOfPhrase !== null && (
        <div
          className="mb-2"
          style={{ fontSize: 11, color: "var(--color-violet-text)" }}
        >
          Même enchaînement que la phrase {dosage.repeatOfPhrase}
        </div>
      )}

      <div
        className="flex items-center"
        style={{ gap: 12, justifyContent: "space-between" }}
      >
        <span className="flex items-center" style={{ gap: 8 }}>
          {phraseChords.map((entry, index) => (
            <span
              key={`${entry.chord.id}-${index}`}
              style={{
                fontSize: 14,
                color:
                  index === walkthrough.currentIndex
                    ? "var(--color-text-primary)"
                    : "var(--color-text-muted)",
                transition: "color 120ms ease",
              }}
            >
              {chordSymbolLabel(entry.chord.symbol, notation)}
            </span>
          ))}
        </span>

        <BottomBar
          phrase={phrase}
          density="flash"
          degraded={walkthrough.degraded}
          onReplay={walkthrough.replay}
          onAcknowledge={onAcknowledge}
        />
      </div>

      {newSymbol !== undefined && (
        <div
          className="mt-2"
          style={{ fontSize: 11, color: "var(--color-violet-text)" }}
        >
          Nouveau ici : {chordSymbolLabel(newSymbol, notation)}
        </div>
      )}

      {teaching && (
        <div
          className="mt-2"
          style={{ fontSize: 11, color: "var(--color-text-muted)" }}
        >
          Cet enchaînement, tu le connais déjà — les prochains, on les passera
          tout seuls.
        </div>
      )}
    </div>
  );
}

// ── Layout primitives ─────────────────────────────────────────────

function KeyBadge({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="font-mono inline-flex items-center justify-center"
      aria-hidden
      style={{
        fontSize: 10,
        minWidth: 18,
        height: 18,
        padding: "0 5px",
        borderRadius: 4,
        background: "rgba(255, 255, 255, 0.08)",
        color: "rgba(255, 255, 255, 0.7)",
      }}
    >
      {children}
    </span>
  );
}

// ── Hooks ─────────────────────────────────────────────────────────

/** Publish the current chord under the S0 slot of the shared hint stack. */
function usePublishChordHint(entry: PhraseChord | null): void {
  const hint = useMemo<Hint | null>(() => {
    const midis = chordHints(entry);

    if (midis.length === 0) {
      return null;
    }

    return { source: HINT_SOURCE, midis, voicing: midis.map((n) => n.midi) };
  }, [entry]);

  useHintPublisher(hint);
}

/**
 * Keyboard shortcuts for S0.
 *
 * - `Enter`       → start the phrase
 * - `Space`       → replay
 * - `←` / `→`     → walk the progression, sounding each chord
 *
 * Every handled key also counts as an interaction, which cancels the
 * flash-mode auto-advance: someone reaching for a key is reading.
 */
function useS0Shortcuts({
  walkthrough,
  onAcknowledge,
  onInteract,
}: {
  walkthrough: S0Walkthrough;
  onAcknowledge: () => void;
  onInteract: () => void;
}): void {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) {
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        onAcknowledge();

        return;
      }

      if (e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        onInteract();
        walkthrough.replay();

        return;
      }

      if (e.key === "ArrowRight") {
        e.preventDefault();
        onInteract();
        walkthrough.step(1);

        return;
      }

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        onInteract();
        walkthrough.step(-1);
      }
    };

    document.addEventListener("keydown", handler);

    return () => document.removeEventListener("keydown", handler);
  }, [onAcknowledge, onInteract, walkthrough]);
}

/**
 * Advance on its own once a flash-mode playback ends.
 *
 * This is what stops the zapping reflex from forming: on a pop song it
 * removes most of the `Entrée` presses, so when a dense screen does come
 * up, the player is still reading rather than hammering through.
 */
function useAutoAdvance({
  enabled,
  isPlaying,
  onAdvance,
}: {
  enabled: boolean;
  isPlaying: boolean;
  onAdvance: () => void;
}): void {
  const startedRef = useRef(false);

  useEffect(() => {
    if (isPlaying) {
      startedRef.current = true;
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!enabled || isPlaying || !startedRef.current) {
      return;
    }

    const timer = setTimeout(onAdvance, AUTO_ADVANCE_MS);

    return () => clearTimeout(timer);
  }, [enabled, isPlaying, onAdvance]);
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tag = target.tagName;

  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
    return true;
  }

  return target.isContentEditable;
}
